import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { Autocomplete, Chip, Dialog, DialogContent, Grid, Tab, Tabs, TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { CustomDialogTitle } from '../../../../../Modules/Common/Custom/CustomDialogTitle';
import { DifferentialDrilldown } from '../../../../../Modules/Common/EmployeeRemuneration/DifferentialDrilldown';
import { EEScorecardAtom } from '../../../../../State/EEScorecard.Atom';
import { EEScorecard } from '../../../../Model/Dto/EmploymentEquity/EEScorecard';
import { Gender, OccupationalLevelTypes, RaceTypes, RemunerationType } from '../../../../Model/Enum/BEE123Enums';
import { ChartType } from '../../../../Model/Enum/ChartType';
import { DashboardSectionType } from '../../../../Model/Enum/DashboardType';
import { MDBox } from '../../MDBox';
import { MDTypography } from '../../MDTypography';
import { DashboardBase } from '../Base/DashboardBase';
import config from '../../../../../config';
import { EmployeeStructureTable } from '../../../../../Modules/Common/EmployeeStructure/EmployeeStructureTable';
import { getDateWithoutTime } from '../../../../../Helpers/DateFormatter';
import { MDInput } from '../../MDInput';
import { EEOccupationalLevelNamingConventionList } from '../../../Constants/Lists/DropdownListOptions';
import AppSelectList, { IDropDownOptions } from '../../FormValidationInputs/AppSelectList';
import { FieldValues, useForm } from 'react-hook-form';
import { TabPanel } from '../../../../../Modules/Base/Account/Account';

const hash = require("object-hash");

interface IProps {
  isVisible: boolean;
  scorecardId: string;
  scorecardTypeId: string;
  isStacked?: boolean;
  isPowerPointPrint?: boolean;
  scorecard: EEScorecard.DtoOut;
  isIncomeDifferentialDashboard: boolean;
  sourceScorecardLookups?: IDropDownOptions[];
  isEEPlanTrackingDashboard: boolean;
  currentDate: Date;
  eePlanDateList? : IDropDownOptions[];
  filterByEePlanDate? : boolean;
}

export const EmploymentEquityDashboard = (props: IProps) => {
  const scorecard = useRecoilValue(EEScorecardAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRemunerationModalOpen, setIsRemunerationModalOpen] = useState(false);
  const [genderIds, setGenderIds] = useState<number[]>([]);
  const [raceIds, setRaceIds] = useState<number[]>([]);
  const [occupationLevelIds, setOccupationLevelIds] = useState<number[]>([]);
  const [remunerationTypeSelected, setRemunerationType] = useState<number>(0);
  const [filterDate, setFilterDate] = useState<Date>(new Date(scorecard.ReportPeriodDateTo));
  const [filterStartDate, setFilterStartDate] = useState<Date | undefined>();
  const [filterEndDate, setFilterEndDate] = useState<Date | undefined>();
  const [selectedSourceScorecardIds, setSelectedSourceScorecardIds] = useState<string[] | undefined>(undefined);
  const [tabValue, setTabValue] = useState<number>(0);
  const [selectedOccupationalLevel,setSelectedOccupationalLevel] = useState<{ id: string | number; label: string }>();
  const [selectedYear,setSelectedYear] = useState<string[]>([]);
  const [selectedYearId,setSelectedYearId] = useState<number>();   
  const [isEEPlanTrackingYear5,setIsEEPlanTrackingYear5] = useState(false);
  const defaultOccupationalLevelId = 1;
  const { control, setValue } = useForm<FieldValues>({
      mode: 'all',
      defaultValues: {
        occupationalLevel: defaultOccupationalLevelId
    },
  });
  const filterByDate = true;
  const isStacked = props.isStacked && true;
  const isPowerPointPrint = props.isPowerPointPrint !== undefined ? props.isPowerPointPrint : false;
  const scorecardEndDate = scorecard.ReportPeriodDateTo;
  const alwaysOutside = true;
  const eePlanDatesList: IDropDownOptions[] = props.eePlanDateList ?? [];
  const chartOptions = { filterDate, isStacked, isPowerPointPrint, scorecard, scorecardEndDate, alwaysOutside, isEEPlanTrackingYear5, selectedOccupationalLevel, selectedYear, selectedYearId};
  const hashString = hash(chartOptions);


  const formatDate = (dateStr: string): string => {
    const parsedDate = new Date(dateStr);
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };


  const openHeadcountModal = (genderIds: Array<number>, raceIds: Array<number>, occupationIds: Array<number>,startDate?: Date, endDate?: Date) => {
    setGenderIds(genderIds);
    setRaceIds(raceIds);
    setOccupationLevelIds(occupationIds);

    if (startDate && endDate) {
      setFilterStartDate(startDate);
      setFilterEndDate(endDate);
    }
    setIsModalOpen(true);
  }

  const openRemunerationModal = (genderIds: Array<number>, raceIds: Array<number>, occupationalLevelIds: Array<number>, remunerationType: number) => {
    setGenderIds(genderIds);
    setRaceIds(raceIds);
    setOccupationLevelIds(occupationalLevelIds);
    setRemunerationType(remunerationType);
    setIsRemunerationModalOpen(true);
  }

  const chartOnClick = (chartType: ChartType, label: string, occupationalLevelIds?: number[]) => {
    if (chartType === ChartType.IncomeDifferentialGraph) {
      chartRemunerationOnClick(label, occupationalLevelIds ?? []);
    }
    else {
      chartHeadcountOnClick(label);
    }
  }

  const handleTabValueChanged = (event: React.SyntheticEvent, newValue: number) => {
    if(newValue === 1){
      setIsEEPlanTrackingYear5(true);
    }
    else {
      setIsEEPlanTrackingYear5(false);
    }
    setTabValue(newValue);
  };

  function tabPropsFunc(index: number) {
      return {
          id: `simple-tab-${index}`,
          'aria-controls': `simple-tabpanel-${index}`,
      };
  }

  const getDefaultEEPlanYear = (eePlanList: IDropDownOptions[]): IDropDownOptions | undefined => {
    if (!eePlanList.length) return undefined;

    const parsedPlans = eePlanList
      .map(item => {
        const match = item.value.match(/\((\d{1,2} \w+ \d{4}) - (\d{1,2} \w+ \d{4})\)/);
        if (!match) return null;

        const start = new Date(match[1]);
        const end = new Date(match[2]);
        return { item, start, end };
      })
      .filter(Boolean) as { item: IDropDownOptions; start: Date; end: Date }[];

    parsedPlans.sort((a, b) => a.start.getTime() - b.start.getTime());

    const inRangePlan = parsedPlans.find(({ start, end }) => props?.currentDate >= start && props?.currentDate <= end);
    if (inRangePlan) return inRangePlan.item;

    if (props?.currentDate < parsedPlans[0].start) return parsedPlans[0].item;

    return parsedPlans[parsedPlans.length - 1].item;
  };

  const defaultSelectedItem = getDefaultEEPlanYear(eePlanDatesList);

  useEffect(() => {
    const selectedItem = EEOccupationalLevelNamingConventionList.find(
      item => item.id === defaultOccupationalLevelId
    );

    if (selectedItem) {
      setSelectedOccupationalLevel({ id: selectedItem.id, label: selectedItem.value });
    }
    if (defaultSelectedItem) {
      const match = defaultSelectedItem.value.match(/\((\d{1,2} \w+ \d{4}) - (\d{1,2} \w+ \d{4})\)/);
      if (match) {
        const formatDate = (date: Date) =>
          `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;

        const start = formatDate(new Date(match[1]));
        const end = formatDate(new Date(match[2]));

        setSelectedYear([start, end]);
      }
        setSelectedYearId(Number(defaultSelectedItem.id))
        setValue("year", Number(defaultSelectedItem.id));
    }
  }, [props?.eePlanDateList]);

  const chartHeadcountOnClick = (label: string) => {
    let optionArray: Array<string> = label
      .split("|")
      .map((option) => option.toLowerCase());
    let genderIds: Array<number> = [];
    let raceIds: Array<number> = [];
    let occupationIds: Array<number> = [];
    for (const option of optionArray) {
      if (option.includes("topmanagement") || option.includes("top management")) {
        occupationIds.push(
          OccupationalLevelTypes.Executive_Director,
          OccupationalLevelTypes.Other_Executive_Management
        );
      }
      else if (option.includes("senior")) {
        occupationIds.push(
          OccupationalLevelTypes.Senior_Management
        );
      }
      else if (option.includes("middle")) {
        occupationIds.push(
          OccupationalLevelTypes.Middle_Management
        );
      }
      else if (option.includes("junior")) {
        occupationIds.push(
          OccupationalLevelTypes.Junior_Management
        );
      }
      else if (option.includes("semi")) {
        occupationIds.push(
          OccupationalLevelTypes.Semi_Skilled
        );
      }
      else if (option.includes("unskilled")) {
        occupationIds.push(
          OccupationalLevelTypes.Unskilled
        );
      }

      if (option.includes("female")) {
        genderIds.push(
          Gender.Female
        )
      }
      else if (option.includes("male")) {
        genderIds.push(
          Gender.Male
        )
      }

      if (
        option.includes("nonsa") ||
        option.includes("nonrsa") ||
        option.includes("nonsouthafrican") ||
        option.includes("non_rsa") ||
        option.includes("foreign national")
      ) {
        raceIds.push(
          RaceTypes.NonRSA
        )
      }
      else if (option.includes("african")) {
        raceIds.push(
          RaceTypes.African
        )
        }
      else if (option.includes("coloured")) {
        raceIds.push(
          RaceTypes.Coloured
        )
      }
      else if (option.includes("white")) {
        raceIds.push(
          RaceTypes.White
        )
      }
      else if (option.includes("indian")) {
        raceIds.push(
          RaceTypes.Indian
        )
      }
    }

    let startDate: Date | undefined;
    let endDate: Date | undefined;
    const possibleDate = label.split("|").pop(); 

    if (possibleDate) {
      const baseDate = new Date(possibleDate);
      if (!isNaN(baseDate.getTime())) { 
        const year = baseDate.getFullYear();
        const month = baseDate.getMonth();
        startDate = new Date(year, month, 1);
        endDate = new Date(year, month + 1, 0);
      }
    }

    openHeadcountModal(genderIds, raceIds, occupationIds, startDate, endDate);
  }

  const chartRemunerationOnClick = (label: string, occupationalLevelIds: number[]) => {
    let optionArray: Array<string> = label
      .split("|")
      .map((option) => option.toLowerCase());
    let showPopup: boolean = true;
    let genderIds: Array<number> = [];
    let raceIds: Array<number> = [];
    let remunerationType: number = 0;

    for (const option of optionArray) {
      if (option.includes("female")) {
        genderIds.push(
          Gender.Female
        )
      }
      else if (option.includes("male")) {
        genderIds.push(
          Gender.Male
        )
      }

      
       if (
        option.includes("nonsa") ||
        option.includes("nonrsa") ||
        option.includes("nonsouthafrican") ||
        option.includes("non_rsa")
      ) {
        raceIds.push(
          RaceTypes.NonRSA
        )
      }
      else if (option.includes("african")) {
        raceIds.push(
          RaceTypes.African
        )
      }
      else if (option.includes("coloured")) {
        raceIds.push(
          RaceTypes.Coloured
        )
      }
      else if (option.includes("white")) {
        raceIds.push(
          RaceTypes.White
        )
      }
      else if (option.includes("indian")) {
        raceIds.push(
          RaceTypes.Indian
        )
      }
      
      if (option.includes("average")) {
        remunerationType = RemunerationType.Average;
        showPopup = false;
      }
      else if (option.includes("high")) {
        remunerationType = RemunerationType.High;
      }
      else if (option.includes("low")) {
        remunerationType = RemunerationType.Low;
      }
      else if (option.includes("median")) {
        remunerationType = RemunerationType.Median;
      }
    }

    if (showPopup) {
      openRemunerationModal(genderIds, raceIds, occupationalLevelIds, remunerationType);
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false);
  }

  const handleCloseRemunerationModal = () => {
    setIsRemunerationModalOpen(false);
  }

  const getAvailableChartElements = () => {
    let chartTypeList: ChartType[] = [];
    if(props.isIncomeDifferentialDashboard && config.enable_income_differentials_graphs === 'true'){
      chartTypeList = [
        ChartType.IncomeDifferential1Analysis,
        ChartType.IncomeDifferential2Analysis,
        ChartType.IncomeDifferentialGraph
      ];
    }
    else if (props.isIncomeDifferentialDashboard && config.enable_income_differentials_graphs === 'false') {
      chartTypeList = [
        ChartType.IncomeDifferentialGraph
      ];
    }
    else if (props.isEEPlanTrackingDashboard) {
      chartTypeList = [
        ChartType.EEPlanTrackingChartAfricanMale,
        ChartType.EEPlanTrackingChartAricanFemale,
        ChartType.EEPlanTrackingChartColouredMale,
        ChartType.EEPlanTrackingChartColouredFemale,
        ChartType.EEPlanTrackingChartIndianMale,
        ChartType.EEPlanTrackingChartIndianFemale,
        ChartType.EEPlanTrackingChartWhiteMale,
        ChartType.EEPlanTrackingChartWhiteFemale,
        ChartType.EEPlanTrackingChartNonRSAMale,
        ChartType.EEPlanTrackingChartNonRSAFemale
      ];
    }
    else {
      chartTypeList = [
        ChartType.EmployeeHeadCountChart1DoubleColumnChart,
        ChartType.EmployeeHeadCount1PieChart,
        ChartType.RecruitmentHeadcountChart,
        ChartType.PromotionHeadcountChart,
        ChartType.TerminationHeadcountChart
      ]
    }


    return chartTypeList;
  }

  useEffect(() => {
    const savedSources = window.sessionStorage.getItem("user-sources");
    if (savedSources) {
      const savedIds = JSON.parse(savedSources) as string[];
      setSelectedSourceScorecardIds(Array.isArray(savedIds) ? savedIds : []);
    }
  }, []);

  return (
    <>
    <Grid container justifyContent="space-between" alignItems="center" mt={3} mb={2}>
      {
        props.isVisible && !props.isIncomeDifferentialDashboard && !props.isEEPlanTrackingDashboard &&
        <Grid item display="flex" alignItems="center">
          <MDBox mb={2} mt={3} display="flex">
            <MDBox mt={0.5}>
              <MDTypography
                variant='button'
                fontWeight='regular'
                fontSize="1rem"
                mr={2}
              >
                View Employees as at
              </MDTypography>
            </MDBox>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDatePicker
                inputFormat="yyyy/MM/dd"
                mask="____/__/__"
                label={"View Employees as at"}
                value={filterDate}
                onChange={(date) => {
                  if (date) {
                    setFilterDate(getDateWithoutTime(date));
                  }
                }}
                renderInput={(params) => <TextField {...params} />}
                views={['year', 'month', 'day']}
              />
            </LocalizationProvider>
          </MDBox>
        </Grid>
      }

      {
        props.isIncomeDifferentialDashboard &&
        <Grid item display="flex" alignItems="center">
        </Grid>
      }

      {
        (props.sourceScorecardLookups?.length ?? 0) > 0 &&
        <Grid item display="flex" alignItems="center">
          <MDBox mt={0.5} display="flex" alignItems="center">
            <MDTypography
              variant='button'
              fontWeight='regular'
              fontSize="1rem"
              mr={2}
            >
              View source scorecard data
            </MDTypography>
          </MDBox>

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={props.sourceScorecardLookups ?? []}
            value={props.sourceScorecardLookups?.filter(source => selectedSourceScorecardIds?.includes(source.id.toString())) ?? []}
            onChange={(event, selectedSources) => {
                if (selectedSources) {
                  const newIds = (selectedSources ?? []).map(s => s.id.toString());
                  setSelectedSourceScorecardIds(newIds);
                  window.sessionStorage.setItem("user-sources", JSON.stringify(newIds));
                }
                else {
                  setSelectedSourceScorecardIds(undefined);
                }
              }
            }
            renderInput={(params) => <MDInput {...params} />}
            getOptionLabel={option => option.value}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{ 
              width: "17rem", 
              height: "1rem", 
              marginBottom: `${(selectedSourceScorecardIds?.length ?? 0) > 6 
                ? 2 + ((selectedSourceScorecardIds?.length ?? 0) - 6) * 1.5
                : 2
                }rem`,
              "& .MuiAutocomplete-tag": {
                maxWidth: "60px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }
             }}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  label={option.value}
                  title={option.value}
                />
              ))
            }
          />
        </Grid>
      }
    </Grid>
   

      {
        props.isVisible && !props.isIncomeDifferentialDashboard && props.isEEPlanTrackingDashboard &&
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Tabs value={tabValue} onChange={handleTabValueChanged} aria-label="Tabs" centered>
              <Tab label="Annual Tracking" {...tabPropsFunc(0)} />
               <Tab label="5-Year Tracking" {...tabPropsFunc(1)} />
              
            </Tabs>
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <AppSelectList
                    control={control}
                    name="occupationalLevel"
                    label="Occupational Level"
                    items={EEOccupationalLevelNamingConventionList}
                    onChange={(selectedId: number) => {
                      const selectedItem = EEOccupationalLevelNamingConventionList.find(item => item.id === selectedId);
                      if (selectedItem) {
                        setSelectedOccupationalLevel({ id: selectedItem.id, label: selectedItem.value });
                      }
                      
                    }}
                  />
                </Grid>
               <Grid item xs={12} sm={3}>
                  <AppSelectList
                    control={control}
                    name="year"
                    label="Year"
                    items={eePlanDatesList}
                    onChange={(selectedId: number) => {
                      const selectedItem = eePlanDatesList.find(item => item.id === selectedId.toString());

                      if (selectedItem) {
                        const matchedDates = selectedItem.value.match(/\((\d{1,2} \w+ \d{4}) - (\d{1,2} \w+ \d{4})\)/);

                        if (matchedDates) {
                          const startDate = formatDate(matchedDates[1]);
                          const endDate = formatDate(matchedDates[2]);

                          setSelectedYear([startDate, endDate]);
                          setSelectedYearId(selectedId);
                        }
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
                 <Grid item xs={7} sm={3}>
                  <AppSelectList
                    control={control}
                    name="occupationalLevel"
                    label="Occupational Level"
                    items={EEOccupationalLevelNamingConventionList}
                    onChange={(selectedId: number) => {
                      const selectedItem = EEOccupationalLevelNamingConventionList.find(item => item.id === selectedId);
                      if (selectedItem) {
                        setSelectedOccupationalLevel({ id: selectedItem.id, label: selectedItem.value });
                      }
                      
                    }}
                  />
                </Grid>
            </TabPanel>
          </Grid>
         
        </Grid>
      }

      <DashboardBase
        key={hashString}
        isVisible={props.isVisible}
        scorecardId={props.scorecardId}
        scorecardTypeId={props.scorecardTypeId}
        heading="Employment Equity Dashboard"
        dashboardSectionType={DashboardSectionType.ee}
        chartOptions={chartOptions}
        onClick={chartOnClick}
        availableChartElements={getAvailableChartElements()}
        sourceScorecardIds={selectedSourceScorecardIds}
        isEEPlanTrackingYear5={isEEPlanTrackingYear5}
      />

      <Dialog disableEnforceFocus open={isModalOpen} onClose={() => { handleCloseModal() }} aria-labelledby="form-dialog-title" maxWidth='xl'>
        <CustomDialogTitle id="customized-dialog-title" onClose={() => handleCloseModal()}>
          {'Employee Structure'}
        </CustomDialogTitle>
        <DialogContent>
          <EmployeeStructureTable 
            scorecardId={props.scorecardId} 
            filterStartDate={filterStartDate} 
            filterEndDate={filterEndDate} 
            filterDate={filterDate} 
            filterByDate={filterByDate} 
            occupationIds={occupationLevelIds} 
            raceIds={raceIds} 
            genderIds={genderIds} 
            sourceScorecardIds={selectedSourceScorecardIds}
            />
        </DialogContent>
      </Dialog>


      <Dialog disableEnforceFocus open={isRemunerationModalOpen} onClose={() => { handleCloseRemunerationModal() }} aria-labelledby="form-dialog-title" maxWidth='xl'>
        <CustomDialogTitle id="customized-remuneration-dialog-title" onClose={() => handleCloseRemunerationModal()}>
          {'Income Differentials'}
        </CustomDialogTitle>
        <DialogContent>
          <DifferentialDrilldown 
            scorecardId={props.scorecardId} 
            genderIds={genderIds} 
            occupationIds={occupationLevelIds} 
            raceIds={raceIds} 
            remunerationType={remunerationTypeSelected} 
            sourceScorecardIds={selectedSourceScorecardIds} 
            />
        </DialogContent>
      </Dialog>
    </>
  )
}

